import * as THREE from 'three';
import GLTFLoader from 'three-gltf-loader';
import Body from './Body';
import Doors from './Doors';
import {
  metals,
  plotnoMat,
  sosnaMat,
  glassMat,
  lightMat,
  lightMatExt,
  BIP,
  BLP,
  CAP,
  JSZ,
  MAG,
  SZ,
  plotnoMatDark,
  plotnoMatDarken,
  shadowMat,
  edgeMat,
  plotnoMatInterior,
} from './Materials';
import { isDeclared } from 'helpers/helpers';

export default class Wardrobe {
  constructor(wardrobeConfig, index, gltf) {
    this.loader = new THREE.TextureLoader();
    this.gltfLoader = new GLTFLoader();
    this.loader.setCrossOrigin('anonymous');
    this.emptyAccesories = true;

    this.counter = 0;

    this.index = parseInt(index);
    this.isCorner = false;
    this.isSlidingFront = !wardrobeConfig.is_hinged;
    this.gltf = gltf;
    this.wardrobeConfig = wardrobeConfig;

    const aName = wardrobeConfig.safo.split('/');

    this.width = 100;
    this.type = aName[0];
    this.id = wardrobeConfig.id;

    switch (this.type) {
      case 'F57-SZFN1D':
        this.width = 103;
        this.isCorner = true;
        break;

      case 'F57-SZF1DN':
        this.width = 97;
        this.isCorner = true;
        break;

      default:
        this.width = parseInt(wardrobeConfig.dimensions.x);
        break;
    }

    this.spacer = 57;
    this.height = wardrobeConfig.dimensions.y;
    this.depth = wardrobeConfig.dimensions.z;

    this.bodyObject = null;
    this.doorsObject = null;
    this.wardrobe = new THREE.Object3D();
    this.wardrobe.name = this.type;
    this.zone_left = new THREE.Group();
    this.zone_center = new THREE.Group();
    this.zone_right = new THREE.Group();
    this.zoneDimensions = [55, 83, 55];
    this.makeWardrobe(wardrobeConfig.safo);
  }

  makeWardrobe() {
    this.bodyObject = new Body(this.wardrobeConfig, this.gltf.clone());

    this.body = this.bodyObject.getBody();
    this.body.name = 'Body';
    this.phantom = this.bodyObject.getPhantom();
    this.phantom.name = 'Phantom_' + this.index;
    this.phantom.visible = false;

    this.wardrobe.add(this.phantom);
    this.wardrobe.add(this.body);

    this.doorsObject = new Doors(this.wardrobeConfig, this.gltf.clone());
    this.doors = this.doorsObject.getDoors();
    this.outlines = this.doorsObject.getOutlines();
    this.wardrobe.add(this.doors);

    this.zones = this.bodyObject.getZones();
    this.zones.visible = false;
    this.wardrobe.add(this.zones);

    this.wardrobe.add(this.zone_left);
    this.wardrobe.add(this.zone_center);
    this.wardrobe.add(this.zone_right);

    this.bodyObject.setBodyShadows(shadowMat);
  }

  setWardrobe(wardrobeConfig) {
    const dimensions = wardrobeConfig.dimensions;
    this.type = wardrobeConfig.safo.split('/')[0];
    this.bodyObject.setBody(wardrobeConfig);
    this.doorsObject.setDoors(wardrobeConfig);
    this.phantom.scale.x = dimensions.x;

    this.phantom.position.z = 2;
    if (this.type === 'F57-SZF1DR') {
      this.phantom.scale.x = dimensions.x + 4;
      this.phantom.position.z = 45;
      this.phantom.position.x = -1;
    }
    if (dimensions.z === 45) {
      this.phantom.position.z = 3;
    }
    this.zone_left.position.x = 2;
    this.zone_left.rotation.y = 0;
    if (dimensions.x > 200) {
      this.zone_center.visible = true;
      this.zone_center.position.x = this.spacer + 1;
      this.zoneDimensions[1] = dimensions.x - this.spacer - this.spacer - 2;
      this.zoneDimensions[0] = this.spacer - 2;
      this.zone_right.position.x = dimensions.x - this.spacer;
      this.zoneDimensions[2] = this.spacer - 2;
    } else {
      this.zone_center.visible = false;
      this.zone_right.position.x = this.spacer + 2;
      this.zoneDimensions[2] = dimensions.x - this.spacer - 3;
      if (dimensions.x > 100) {
        this.zoneDimensions[0] = 55;
        this.zone_right.visible = true;
      } else {
        this.zone_right.visible = false;
      }
      if (dimensions.x === 50) {
        this.zoneDimensions[0] = 46;
      }
      if (dimensions.x === 75) {
        this.zoneDimensions[0] = 72;
      }
      if (dimensions.x === 100) {
        this.zone_right.visible = true;
        this.zoneDimensions[0] = 48;
        this.zoneDimensions[2] = 48;
        this.zone_right.position.x = 51;
      }
      if (dimensions.x === 103) {
        this.zone_right.visible = false;
        this.zoneDimensions[0] = 100;
        this.zone_left.position.x = 28;
        this.zone_left.position.z = -66;
        this.zone_left.rotation.y = (-90 * Math.PI) / 180;
      }
      if (dimensions.x === 97) {
        this.zoneDimensions[0] = 93;
        this.zone_left.position.z = 0;
        this.zone_left.visible = true;
      }
      if (dimensions.z === 97) {
        this.zone_left.visible = true;
        this.zoneDimensions[0] = 93;
        this.zone_left.position.z = -45;
        this.zone_left.rotation.y = (-90 * Math.PI) / 180;
      }
    }
  }

  showPhantom(how) {
    this.phantom.visible = how;
  }

  getPhantom() {
    return this.phantom;
  }

  getZones() {
    return this.zones;
  }

  getFronts() {
    return this.doors;
  }

  setBodyMaterial(material) {
    this.loader.load(
      material,
      // 'textures/sonoma_1024.jpg',
      texture => {
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;

        const newMaterial = new THREE.MeshBasicMaterial({
          map: texture,
          // shininess: 100,
          // specular: 0xffffff,
        });
        this.body.children.forEach(obj => {
          const aName = obj.name.split('_');

          if (aName[0] === 'Korpus') {
            this.bodyObject.changeMaterial(newMaterial);
          }
        });
      },

      // onError callback
      function (err) {
        // console.error('setBodyMaterial error happened.', err);
      },
    );
  }

  setFrontsMaterial(frontList, is_hinged) {
    if (this.doors.hasOwnProperty('children') && frontList.length > 0) {
      let fronts = this.doors.children.find(item => item.name === 'Fronts');
      if (typeof fronts !== 'undefined' && frontList.length > 1 && is_hinged === false) {
        fronts.children.forEach((front, index) => {
          const position = front.name.split('_');

          let frontFromList = frontList[position[1]][position[2]];

          if (isDeclared(frontFromList)) {
            let matSymbol = null;
            if (frontFromList !== undefined) {
              let aSymbol = frontFromList.slug.split('_');
              matSymbol = aSymbol[aSymbol.length - 1].toUpperCase();
            }

            switch (matSymbol) {
              case 'BIP':
                front.material = BIP;
                break;
              case 'BLP':
                front.material = BLP;
                break;
              case 'CAP':
                front.material = CAP;
                break;
              case 'JSZ':
                front.material = JSZ;
                break;
              case 'MAG':
                front.material = MAG;
                break;
              case 'SZ':
                front.material = SZ;
                break;
              default:
                if (isDeclared(frontFromList) && frontFromList.hasOwnProperty('img')) {
                  this.loader.load(
                    frontFromList.img,
                    texture => {
                      texture.wrapS = THREE.RepeatWrapping;
                      texture.wrapT = THREE.RepeatWrapping;
                      if (
                        (position[1] === '0' && position[2] === '0') ||
                        (position[1] === '1' && position[2] === '2')
                      ) {
                        texture.offset.set(0.25, 0);
                      }
                      if (
                        (position[1] === '0' && position[2] === '3') ||
                        (position[1] === '1' && position[2] === '1')
                      ) {
                        texture.offset.set(0.75, 0);
                        texture.repeat.set(1, -1);
                      }
                      if (
                        (position[1] === '0' && position[2] === '1') ||
                        (position[1] === '1' && position[2] === '0')
                      ) {
                        texture.offset.set(-0.25, 0);
                        texture.repeat.set(-1, -1);
                      }
                      if (
                        (position[1] === '0' && position[2] === '2') ||
                        (position[1] === '1' && position[2] === '3')
                      ) {
                        texture.offset.set(-0.3, 0);
                        texture.repeat.set(-1, 1);
                      }
                      front.material = new THREE.MeshPhongMaterial({
                        map: texture,
                        shininess: 0.3,
                        // specular: 0x000000,
                      });
                    },
                    // onProgress callback currently not supported
                    undefined,
                    // onError callback
                    function (err) {
                      // console.error('setFronts happened', err);
                    },
                  );
                } else {
                  front.material = MAG;
                }
                break;
            }
          }
        });
      } else {
        if (typeof fronts !== 'undefined' && frontList.length > 0) {
          let frontsFromList = [frontList[0], frontList[1]];

          for (let i = 0; i < frontsFromList.length; i++) {
            if (
              frontsFromList[i] !== undefined &&
              frontsFromList[i] !== null &&
              frontsFromList[i].hasOwnProperty('img')
            ) {
              let matSymbol = null;
              let aSymbol = frontsFromList[i].slug.split('_');
              matSymbol = aSymbol[aSymbol.length - 1].toUpperCase();

              let type = 'Lus';
              if (frontsFromList[i].types.hasOwnProperty(14)) {
                // ramiaki
                type = 'Ram';
              }

              let frontMaterial = null;

              switch (matSymbol) {
                case 'BIP':
                  frontMaterial = BIP;
                  fronts.children.forEach(front => {
                    let aName = front.name.split('_');
                    if (parseInt(aName[1]) === i) {
                      front.material = frontMaterial;
                    }
                  });
                  break;
                case 'BLP':
                  frontMaterial = BLP;
                  fronts.children.forEach(front => {
                    let aName = front.name.split('_');
                    if (parseInt(aName[1]) === i) {
                      front.material = frontMaterial;
                    }
                  });
                  break;
                case 'CAP':
                  frontMaterial = CAP;
                  fronts.children.forEach(front => {
                    let aName = front.name.split('_');
                    if (parseInt(aName[1]) === i) {
                      front.material = frontMaterial;
                    }
                  });
                  break;
                case 'JSZ':
                  frontMaterial = JSZ;
                  fronts.children.forEach(front => {
                    let aName = front.name.split('_');
                    if (parseInt(aName[1]) === i) {
                      front.material = frontMaterial;
                    }
                  });
                  break;
                case 'MAG':
                  frontMaterial = MAG;
                  fronts.children.forEach(front => {
                    let aName = front.name.split('_');
                    if (parseInt(aName[1]) === i) {
                      front.material = frontMaterial;
                    }
                  });
                  break;
                case 'SZ':
                  frontMaterial = SZ;
                  fronts.children.forEach(front => {
                    let aName = front.name.split('_');
                    if (parseInt(aName[1]) === i) {
                      front.material = frontMaterial;
                    }
                  });
                  break;
                default:
                  this.loader.load(
                    frontsFromList[i].img,
                    texture => {
                      frontMaterial = new THREE.MeshPhongMaterial({
                        map: texture,
                        shininess: 0.5,
                        // specular: 0x000000,
                      });

                      texture.repeat.set(1, 1);
                      texture.wrapS = THREE.RepeatWrapping;
                      texture.wrapT = THREE.RepeatWrapping;

                      fronts.children.forEach(front => {
                        let aName = front.name.split('_');
                        if (parseInt(aName[1]) === i) {
                          front.material = frontMaterial;
                        }
                      });
                    },
                    // onProgress callback currently not supported
                    undefined,
                    // onError callback
                    function (err) {
                      // console.error('setFronts happened', err);
                    },
                  );
              }
            }
          }
        }
      }
    }
  }

  setSelectedFronts(frontSelection) {
    this.outlines.children.forEach((outline, index) => {
      const outlineName = outline.name.split('-');
      if (frontSelection.includes(outlineName[1])) {
        outline.visible = true;
      } else {
        outline.visible = false;
      }
    });
  }

  clearAccessoriesAll() {
    this.clearAccessories(this.zone_left);
    this.clearAccessories(this.zone_center);
    this.clearAccessories(this.zone_right);
  }

  clearAccessories(zone) {
    for (let i = zone.children.length - 1; i >= 0; i--) {
      zone.remove(zone.children[i]);
      zone.children.forEach(mesh => {
        if (mesh.type === 'Mesh') {
          mesh.geometry.dispose();
        }
      });
    }
  }

  setAccessories(accessories, externalLights = false) {
    // console.log('Set accessories',this.wardrobeConfig);

    if (
      this.type !== 'F57-SZFN1D' &&
      this.type !== 'F57-SZF1DL' &&
      this.type !== 'F57-SZF1DR' &&
      externalLights === true &&
      this.wardrobeConfig.external_light === true
    ) {
      const lightLoader = new GLTFLoader();
      lightLoader.setCrossOrigin(undefined);
      lightLoader.load(`${process.env.PUBLIC_URL}/gltf/f57_l99pst.glb`, gltf => {
        gltf.scene.scale.set(100, 100, 100);
        gltf.scene.position.y = this.wardrobeConfig.dimensions.y;

        gltf.scene.traverse(obj => {
          switch (obj.name) {
            case 'light':
              obj.material = lightMatExt;
              break;
            default:
          }
        });
        if (this.wardrobeConfig.dimensions.x > 120) {
          gltf.scene.position.x = this.wardrobeConfig.dimensions.x / 4;
          const second = gltf.scene.clone();
          second.position.x =
            this.wardrobeConfig.dimensions.x - this.wardrobeConfig.dimensions.x / 4;
          this.zone_left.add(second);
        } else {
          gltf.scene.position.x = this.wardrobeConfig.dimensions.x / 2;
        }
        this.zone_left.add(gltf.scene);
      });
    }
    this.counter++;

    Object.entries(accessories).forEach(zoneAccessories => {
      let zoneIndex = 0;
      let accessoryCounter = 0;
      switch (zoneAccessories[0]) {
        case 'left':
          zoneIndex = 0;
          this.clearAccessories(this.zone_left);
          break;
        case 'center':
          this.clearAccessories(this.zone_center);
          zoneIndex = 1;
          break;
        case 'right':
          this.clearAccessories(this.zone_right);
          zoneIndex = 2;
          break;
        default:
      }

      // console.log(zoneAccessories,this.zoneDimensions);

      if (zoneAccessories[1]) {
        zoneAccessories[1].forEach(accessory => {
          if (accessory.slug) {
            const accessoryType = accessory.safo.split('/')[0];
            const accessoryCode = accessoryType.split('-')[1];
            const symbol = accessoryCode.substring(2, 0);
            const number = accessoryCode.substring(6);

            let model = `${process.env.PUBLIC_URL}/gltf/${accessory.slug}.glb`;

            switch (accessoryType) {
              case 'F57-DR':
                model = `${process.env.PUBLIC_URL}/gltf/f57_dr.glb`;
                break;
              case 'F57-POL3':
                model = `${process.env.PUBLIC_URL}/gltf/f57_pol3.glb`;
                break;
              case 'F57-POL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_pol.glb`;
                break;
              case 'F57-POLB':
                model = `${process.env.PUBLIC_URL}/gltf/f57_polb.glb`;
                break;
              case 'F57-POLST':
                model = `${process.env.PUBLIC_URL}/gltf/f57_polst.glb`;
                break;
              case 'F57-ZD300X1-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              case 'F57-ZG300X1-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              case 'F57-ZD400X1-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              case 'F57-ZG400X1-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              case 'F57-ZG400X2-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              case 'F57-ZD500X1-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              case 'F57-ZG500X1-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              case 'F57-ZG300X2-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              case 'F57-ZG500X2-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              case 'F57-ZG800X1-AL':
                model = `${process.env.PUBLIC_URL}/gltf/f57_zg100.glb`;
                break;
              default:
            }

            const gltfLoader = new GLTFLoader();
            gltfLoader.setCrossOrigin(undefined);
            gltfLoader.load(
              model,
              gltf => {
                this.loaded = true;
                gltf.scene.traverse(obj => {
                  switch (obj.name) {
                    case 'sosna':
                      obj.material = sosnaMat;
                      break;
                    case 'glass':
                      obj.material = glassMat;
                      break;
                    case 'chrome':
                      obj.material = metals.SRE;
                      break;
                    case 'dark':
                      obj.material = plotnoMatDark;
                      break;
                    case 'darken':
                      obj.material = plotnoMatDarken;
                      break;
                    case 'shadowed':
                      obj.material = plotnoMatInterior;
                      break;
                    case 'lamp':
                      obj.material = edgeMat;
                      break;
                    case 'light':
                      obj.material = lightMat;
                      break;
                    case 'lightbox':
                      if (this.depth < 66) {
                        obj.rotation.y = Math.PI / 2;
                        obj.scale.z = 3;
                        obj.scale.x = 0.3;
                        obj.position.x = 0.2;
                        obj.position.z = -0.27;
                      }
                      obj.material = plotnoMatInterior;
                      break;
                    case 'lightstripe':
                      obj.material = lightMat;
                      break;
                    case 'F57_POLGH':
                      obj.material = plotnoMatInterior;
                      break;
                    case 'F57-DR_R':
                      obj.position.x = this.zoneDimensions[zoneIndex] / 100 - 0.01;
                      break;
                    case 'F57-DR':
                      obj.scale.x = this.zoneDimensions[zoneIndex] / 100 - 0.02;
                      break;
                    case 'F57-POLST_H':
                      obj.material = plotnoMatInterior;
                      obj.position.x = 0.02;
                      obj.scale.x = this.zoneDimensions[zoneIndex] / 100 + 0.02;
                      break;
                    case 'F57-POLST_V':
                      obj.material = plotnoMatInterior;
                      obj.position.x = this.zoneDimensions[zoneIndex] / 200;
                      break;
                    default:
                      obj.material = plotnoMat;
                  }
                  switch (accessoryType) {
                    case 'F57-DR':
                      obj.material = metals.SRE;
                      break;
                    case 'F57-SZU3S':
                      obj.material = plotnoMatInterior;
                      break;
                    case 'F57-POL3':
                      obj.material = plotnoMatInterior;
                      obj.scale.x = this.zoneDimensions[zoneIndex] / 100;
                      break;
                    case 'F57-POL':
                      obj.material = plotnoMatInterior;
                      obj.scale.x = this.zoneDimensions[zoneIndex] / 100;
                      break;
                    case 'F57-POLB':
                      obj.material = plotnoMatInterior;
                      obj.scale.x = this.zoneDimensions[zoneIndex] / 100;
                      break;
                    default:
                  }
                });

                switch (accessory.slug) {
                  case 'f57_ktn3s_55_tx':
                    gltf.scene.position.x = this.zoneDimensions[zoneIndex] - 56;
                    break;
                  default:
                }

                let duplication = null;

                gltf.scene.scale.set(100, 100, 100);

                if (symbol === 'ZD' || symbol === 'ZG') {
                  const size = accessoryCode.substr(2, 2);
                  gltf.scene.scale.x = size;
                  gltf.scene.position.x = this.zoneDimensions[zoneIndex] / 2;
                  if (this.type === 'F57-SZF1DL') {
                    gltf.scene.position.x = this.zoneDimensions[zoneIndex] / 2 + 14;
                  }
                  if (number === '2') {
                    gltf.scene.position.x = this.zoneDimensions[zoneIndex] / 4 + 5;

                    duplication = gltf.scene.clone();
                    duplication.position.x =
                      this.zoneDimensions[zoneIndex] - this.zoneDimensions[zoneIndex] / 4 - 5;
                    duplication.position.y = accessory.altitude;
                    if (this.depth < 50) {
                      duplication.position.x =
                        this.zoneDimensions[zoneIndex] - this.zoneDimensions[zoneIndex] / 4 + 10;
                    }
                  }
                  if (this.depth < 50 && zoneAccessories[0] === 'right') {
                    gltf.scene.position.x = 25;
                  }
                }

                gltf.scene.position.y = accessory.altitude;

                gltf.scene.name = accessory.slug + '_' + zoneIndex + '_' + accessoryCounter;
                switch (zoneAccessories[0]) {
                  case 'left':
                    this.zone_left.add(gltf.scene);
                    break;
                  case 'center':
                    this.zone_center.add(gltf.scene);
                    if (duplication !== null) {
                      this.zone_center.add(duplication);
                    }
                    break;
                  case 'right':
                    this.zone_right.add(gltf.scene);
                    if (duplication !== null) {
                      this.zone_right.add(duplication);
                    }
                    break;
                  default:
                }
                accessoryCounter++;
              },
              xhr => {
                // called while loading is progressing
                // let progres = xhr.total ? (xhr.loaded / xhr.total) * 100 : 100;
                // console.log(`Acc. ${progres}% loaded`);
              },
              error => {
                // called when loading has errors
                // console.error('An error happened', error);
              },
            );
          }
        });
      }
    });
  }

  setHandlesMaterial(handles = {}) {
    if (this.isSlidingFront) {
      let rails = this.doorsObject.getHandles();
      if (rails !== undefined && handles.left) {
        rails.children.forEach(rail => {
          const handleMatName = handles.left.safo.split('-').pop();
          rail.material = metals[handleMatName];
        });
      }
    }
  }

  openDoors(how) {
    this.doors.visible = how;
  }

  getWardrobe() {
    return this.wardrobe;
  }

  getWidth() {
    return this.width;
  }
}
